/**
 * PREVIEW — Inbox Triage (roadmap concept, not built).
 *
 * A mockup of an agent that classifies inbound email, ranks it by priority, and
 * drafts replies for human approval (read-and-draft only — never sends
 * unattended). Shows a triaged priority queue, a category breakdown, and a
 * sample AI-drafted reply. All data is hardcoded sample data; the honest
 * PreviewBanner makes clear this isn't live.
 */
import { useState } from "react";
import { Panel } from "../../components/salience/Panel";
import {
  BarRow,
  Pill,
  PreviewBanner,
  PreviewHeader,
  StatTile,
} from "../../components/preview/primitives";

// ---- Sample data (illustrative only) --------------------------------------

type Category = "press" | "business" | "fan" | "spam";
type Priority = "high" | "med" | "low";

interface Mail {
  from: string;
  org: string;
  subject: string;
  category: Category;
  priority: Priority;
  age: string;
  preview: string;
}

const QUEUE: Mail[] = [
  {
    from: "Eleanor Hughes",
    org: "BBC Newsnight",
    subject: "Interview request — wealth tax segment, Thursday",
    category: "press",
    priority: "high",
    age: "1h",
    preview:
      "We're running a segment on the wealth-tax debate this Thursday and would love to have Gary on. Could he do a 7-minute live spot at 22:40?",
  },
  {
    from: "Marcus Bell",
    org: "Penguin Random House",
    subject: "Trading Game — foreign rights, German edition",
    category: "business",
    priority: "high",
    age: "3h",
    preview:
      "Following up on the German-language rights. Our co-agent in Frankfurt has a firm offer and a deadline of Friday next week.",
  },
  {
    from: "Sophie Adeyemi",
    org: "—",
    subject: "Your video changed how I think about my pension",
    category: "fan",
    priority: "med",
    age: "5h",
    preview:
      "I just wanted to say thank you — I'd never understood how inflation eats savings until your last video. I've moved my pension allocation.",
  },
  {
    from: "events@lse.ac.uk",
    org: "LSE",
    subject: "Guest lecture invitation — Michaelmas term",
    category: "business",
    priority: "med",
    age: "9h",
    preview:
      "We'd be delighted to invite Gary to give a guest lecture to our second-year economics cohort this autumn. Honorarium and travel covered.",
  },
  {
    from: "winner-notice",
    org: "—",
    subject: "RE: RE: your account has been selected!!",
    category: "spam",
    priority: "low",
    age: "11h",
    preview: "Congratulations! You have been pre-approved for an exclusive...",
  },
];

const CATEGORIES = [
  { label: "Press / media", value: 38, colour: "var(--color-agenda-media)" },
  { label: "Business / partnerships", value: 24, colour: "var(--color-agenda-elite)" },
  { label: "Audience / fan mail", value: 112, colour: "var(--color-up)" },
  { label: "Spam / filtered", value: 67, colour: "var(--color-down)" },
];

const DRAFT = `Hi Eleanor,

Thanks for thinking of Gary for Thursday's segment — the wealth-tax
debate is right in his wheelhouse.

A 22:40 live spot works in principle. Two quick things before we
confirm:

  • Remote from his usual setup, or in-studio? (Remote is easier to
    lock at short notice.)
  • Could you share the other guests / the framing? Gary prefers to
    debate the mechanics rather than do a soundbite back-and-forth.

If those line up he's a yes. Shall I pencil it in?

Best,
Oscar`;

const CATEGORY_TONE: Record<Category, Category> = {
  press: "press",
  business: "business",
  fan: "fan",
  spam: "spam",
};

export function InboxPreview() {
  const [selected, setSelected] = useState(0);
  const catMax = Math.max(...CATEGORIES.map((c) => c.value));
  const mail = QUEUE[selected];

  return (
    <div>
      <PreviewBanner module="Inbox Triage" />
      <PreviewHeader
        kicker="future module · agent 4"
        title="Inbox Triage"
        blurb="An agent that reads the inbound flood, sorts it by what actually needs a human, and drafts replies for approval — so press requests and real opportunities never sit unseen under the fan mail and the spam. Read-and-draft only; nothing sends without a person clicking go."
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile kicker="Inbox" value="241" label="unread" sub="last 7 days" />
        <StatTile kicker="Today" value="38" label="triaged" accent="up" />
        <StatTile kicker="Drafts" value="12" label="ready to review" sub="awaiting approval" />
        <StatTile kicker="Flagged" value="2" label="high priority" accent="down" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Panel
          title="Priority queue"
          eyebrow="Triaged & ranked"
          hint="Classified by sender, intent and urgency. High-priority press and business land at the top; spam drops out. Click a row to see the agent's drafted reply."
        >
          <ul className="divide-y divide-white/10">
            {QUEUE.map((m, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => setSelected(i)}
                  className={`flex w-full flex-col gap-1 px-2 py-2.5 text-left transition-colors ${
                    selected === i ? "bg-white/10" : "hover:bg-white/5"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Pill tone={CATEGORY_TONE[m.category]}>{m.category}</Pill>
                    <Pill tone={m.priority}>{m.priority}</Pill>
                    <span className="ml-auto font-mono text-[11px] text-terminal-ink-muted">
                      {m.age}
                    </span>
                  </div>
                  <div className="wrap-break-word text-[13px] font-semibold text-terminal-ink">
                    {m.subject}
                  </div>
                  <div className="wrap-break-word text-[11px] text-terminal-ink-muted">
                    {m.from}
                    {m.org !== "—" && ` · ${m.org}`}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        <div className="flex flex-col gap-6">
          <Panel
            title="Drafted reply"
            eyebrow="Awaiting your approval"
            hint="The agent drafts in Oscar's voice from the thread context. A human edits and sends — the agent never sends on its own."
          >
            <div className="mb-2 rounded-lg border border-white/10 bg-terminal-panel p-2.5 text-[12px]">
              <div className="text-terminal-ink-muted">
                Re: <span className="text-terminal-ink">{mail.subject}</span>
              </div>
              <div className="mt-0.5 text-terminal-ink-muted">
                to {mail.from}
                {mail.org !== "—" && ` · ${mail.org}`}
              </div>
            </div>
            <pre className="max-h-[280px] overflow-y-auto whitespace-pre-wrap wrap-break-word rounded-lg border border-white/10 bg-terminal p-3 font-sans text-[12.5px] leading-relaxed text-terminal-ink dark-scrollbar">
              {selected === 0 ? DRAFT : "[ select the Newsnight request to see a sample drafted reply ]"}
            </pre>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="rounded bg-up/20 px-3 py-1.5 text-[12px] font-semibold text-up opacity-60">
                Approve & send
              </span>
              <span className="rounded border border-white/15 px-3 py-1.5 text-[12px] font-semibold text-terminal-ink-muted opacity-60">
                Edit
              </span>
              <span className="ml-auto text-[10px] uppercase tracking-wider text-terminal-ink-muted">
                buttons inert in preview
              </span>
            </div>
          </Panel>

          <Panel
            title="Inbound mix — 7 days"
            eyebrow="What's coming in"
            hint="The split the agent is sorting. Fan mail is the bulk; the value is never missing the press/business needle in that haystack."
          >
            <div className="space-y-2.5">
              {CATEGORIES.map((c) => (
                <BarRow
                  key={c.label}
                  label={c.label}
                  value={c.value}
                  max={catMax}
                  colour={c.colour}
                />
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
